/**
 * Dynamic Menu Configuration for Nested SideNav
 *
 * This configuration supports:
 * - Multi-level nested menus with infinite depth
 * - Dynamic icon replacement when navigating into submenus
 * - Module-based filtering for spaces
 * - Clean separation between root and space contexts
 *
 * Menu Flow:
 * 1. /manage-spaces → Shows root menu (planet icon only)
 * 2. Click "Manage" on space → Navigate to /manage-space-dashboard
 * 3. Sidebar shows: Home icon + Space logo + Module icons (Dashboard, Events, Coupons, etc.)
 * 4. Click on module with submenu (e.g., Events) → Sidebar icons replace with submenu items
 */

import { CouponsIcon } from '@/assets/icon/CouponsIcon'
import { RiPlanetLine } from '@/assets/icon/menu/RiPlanetLine'
import CampaignIcon from '@/assets/icon/svgs/CampaignIcon'
import CategoriesIcon from '@/assets/icon/svgs/CategoriesIcon'
import CommunicationSvg from '@/assets/icon/svgs/communication'
import CouponSvg from '@/assets/icon/svgs/coupons-svg'
import DashboardSvg from '@/assets/icon/svgs/dashboard-svg'
import DonationIcon from '@/assets/icon/svgs/DonationIcon'
import EventsSvg from '@/assets/icon/svgs/events-svg'
import FundraisingSvg from '@/assets/icon/svgs/fundraising-svg'
import GroupIcon from '@/assets/icon/svgs/GroupIcon'
import KioskSvg from '@/assets/icon/svgs/kiosk-svg'
import PlaylistIcon from '@/assets/icon/svgs/PlaylistIcon'
import SalahListsSvg from '@/assets/icon/svgs/salah-lists-svg'
import SalahMonthlySvg from '@/assets/icon/svgs/salah-monthly-svg'
import SalahSettingsSvg from '@/assets/icon/svgs/salah-settings-svg'
import SalahSvg from '@/assets/icon/svgs/salah-svg'
import ScreenSvg from '@/assets/icon/svgs/screen-svg'
import SettingsSvg from '@/assets/icon/svgs/settings-svg'
import TeamSvg from '@/assets/icon/svgs/team-svg'
import {
  hasAnyPermission,
  isTheActionPermitted,
} from '@/libs/utils/userPermissions/userPermissions'

const envType = process.env.NEXT_PUBLIC_PROJECT_ENV

/**
 * Menu Item Structure:
 * {
 *   id: string (unique identifier)
 *   label: string (display name)
 *   icon: ReactNode (icon component)
 *   link?: string (navigation path - optional)
 *   badge?: number (notification badge count)
 *   line?: boolean (show separator line after item)
 *   submenu?: string|array (key reference to submenu config OR inline array)
 *   iconReplacement?: ReactNode (icon to show in parent when this submenu item is active)
 *   metadata?: object (additional data like space info, permissions, etc.)
 * }
 */
const screensLink =
  hasAnyPermission('screens', 'viewPlaylists') ||
  hasAnyPermission('screens', 'managePlaylists')
    ? '/manage-screens/playlists'
    : hasAnyPermission('screens', 'viewScreens') ||
        hasAnyPermission('screens', 'manageScreens')
      ? '/manage-screens/screens'
      : envType !== 'prod'
        ? '/manage-screens/settings'
        : null

const fundraisingsLink =
  ((hasAnyPermission('fundraising', 'viewFundraisingDashboard') ||
    hasAnyPermission('fundraising', 'manageFundraisingDashboard')) &&
    '/manage-fundraising/dashboard') ||
  ((hasAnyPermission('fundraising', 'viewCampaigns') ||
    hasAnyPermission('fundraising', 'manageCampaigns')) &&
    '/manage-fundraising/campaign') ||
  ((hasAnyPermission('fundraising', 'viewCategories') ||
    hasAnyPermission('fundraising', 'manageCategories')) &&
    '/manage-categories/categories') ||
  ((hasAnyPermission('fundraising', 'viewDonations') ||
    hasAnyPermission('fundraising', 'manageDonations')) &&
    '/manage-fundraising/donations') ||
  ((hasAnyPermission('fundraising', 'viewDonors') ||
    hasAnyPermission('fundraising', 'manageDonors')) &&
    '/manage-fundraising/donors') ||
  (envType !== 'prod' &&
    hasAnyPermission('fundraising', 'fundraisingSettings') &&
    '/manage-fundraising/settings') ||
  null
const spaceData =
  typeof window !== 'undefined'
    ? JSON.parse(localStorage.getItem('spaceData') || '{}')
    : {}

export const DYNAMIC_MENU_CONFIG = {
  /**
   * ROOT MENU - Shown on /manage-spaces
   * Only displays the planet icon for "My Spaces"
   */
  root: [
    {
      id: 'spaces',
      label: 'My Spaces',
      icon: <RiPlanetLine />,
      link: '/manage-spaces',
      submenu: 'spaceModules', // When user clicks "Manage" on a space, switch to this menu
    },
  ],

  /**
   * SPACE MODULES MENU - Shown when user is inside a space
   * Displays after clicking "Manage" on any space
   * Filtered by space.enabledModules
   */
  spaceModules: [
    isTheActionPermitted('dashboard') && {
      id: 'space-dashboard',
      label: 'Dashboard',
      icon: <DashboardSvg />,
      link: '/manage-space-dashboard',
    },
    isTheActionPermitted('events') && {
      id: 'events',
      label: 'Events',
      icon: <EventsSvg style={{ width: '18px' }} />,
      link:
        hasAnyPermission('events', 'viewEvents') ||
        hasAnyPermission('events', 'manageEvents')
          ? '/manage-events/events'
          : '/manage-events/tickets-management',
      submenu: 'eventSubmenu', // Reference to nested submenu
    },
    isTheActionPermitted('salah') && {
      id: 'salah',
      label: 'Salah',
      icon: <SalahSvg />,
      link: '/manage-salah/salah',
      submenu: 'salahSubmenu',
    },
    isTheActionPermitted('fundraising') && {
      id: 'fundraising',
      label: 'Fundraising',
      icon: <FundraisingSvg />,
      link: fundraisingsLink,
      submenu: 'fundraisingSubmenu',
    },
    isTheActionPermitted('kiosk') && {
      id: 'devices',
      label: 'Kiosk',
      icon: <KioskSvg />,
      link: '/manage-devices',
    },
    isTheActionPermitted('screens') && {
      id: 'screens',
      label: 'Screens',
      icon: <ScreenSvg style={{ width: '18px' }} />,
      link: screensLink,
      submenu: 'screensSubmenu',
    },
    isTheActionPermitted('coupons') && {
      id: 'coupons',
      label: 'Coupons',
      icon: <CouponSvg />,
      link: '/manage-coupons/coupons',
      // submenu: "couponsSubmenu",
    },
    isTheActionPermitted('settings') && {
      id: 'settings',
      label: 'Settings',
      icon: <SettingsSvg />,
      link: hasAnyPermission('settings', 'manageTeam')
        ? '/manage-settings/team'
        : envType !== 'prod'
          ? '/manage-settings/communications'
          : null,
      submenu: 'manageSettingsSubmenu',
    },
  ]?.filter(Boolean),

  /**
   * EVENT SUBMENU - Shown when Events module is clicked
   * When user clicks on Events, the sidebar icons are replaced with these submenu items
   */
  eventSubmenu: [
    // {
    //   id: "event-home",
    //   label: "Home",
    //   icon: <DashboardIcon />,
    //   link: "/manage-events/event-home",
    //   iconReplacement: <DashboardIcon />,
    // },
    (hasAnyPermission('events', 'viewEvents') ||
      hasAnyPermission('events', 'manageEvents')) && {
      id: 'event-events',
      label: 'Events',
      icon: <EventsSvg style={{ width: '18px' }} />,
      link: '/manage-events/events',
      iconReplacement: <EventsSvg style={{ width: '18px' }} />,
    },
    (hasAnyPermission('events', 'viewTickets') ||
      hasAnyPermission('events', 'manageTickets')) && {
      id: 'event-tickets',
      label: 'Tickets',
      icon: <CouponsIcon />,
      link: '/manage-events/tickets-management',
      iconReplacement: <CouponsIcon />,
    },
  ]?.filter(Boolean),

  /**
   * COUPONS SUBMENU - Shown when Coupons module is clicked
   * Add more submenus as needed for other modules
   */
  couponsSubmenu: [
    // {
    //   id: "coupons-home",
    //   label: "Home",
    //   icon: <DashboardIcon />,
    //   link: "/manage-coupons/coupons",
    //   iconReplacement: <DashboardIcon />,
    // },
    // Add more coupon submenu items here
  ],

  /**
   * FUNDRAISING SUBMENU - Shown when Fundraising module is clicked
   */
  fundraisingSubmenu: [
    (hasAnyPermission('fundraising', 'viewFundraisingDashboard') ||
      hasAnyPermission('fundraising', 'manageFundraisingDashboard')) && {
      id: 'fundraising-dashboard',
      label: 'Dashboard',
      icon: <DashboardSvg />,
      link: '/manage-fundraising/dashboard',
      iconReplacement: <DashboardSvg />,
    },
    (hasAnyPermission('fundraising', 'viewCampaigns') ||
      hasAnyPermission('fundraising', 'manageCampaigns')) && {
      id: 'fundraising-campaign',
      label: 'Campaign',
      icon: <CampaignIcon />,
      link: '/manage-fundraising/campaign',
      iconReplacement: <CampaignIcon />,
    },
    (hasAnyPermission('fundraising', 'viewCategories') ||
      hasAnyPermission('fundraising', 'manageCategories')) && {
      id: 'fundraising-categories',
      label: 'Categories',
      icon: <CategoriesIcon />,
      link: '/manage-categories/categories',
      iconReplacement: <CategoriesIcon />,
    },
    (hasAnyPermission('fundraising', 'viewDonations') ||
      hasAnyPermission('fundraising', 'manageDonations')) && {
      id: 'fundraising-donations',
      label: 'Donations',
      icon: <DonationIcon />,
      link: '/manage-fundraising/donations',
      iconReplacement: <DonationIcon />,
    },
    (hasAnyPermission('fundraising', 'viewDonors') ||
      hasAnyPermission('fundraising', 'manageDonors')) && {
      id: 'fundraising-donors',
      label: 'Donors',
      icon: <GroupIcon />,
      link: '/manage-fundraising/donors',
      iconReplacement: <GroupIcon />,
    },
    envType !== 'prod' &&
      hasAnyPermission('fundraising', 'fundraisingSettings') && {
        id: 'fundraising-settings',
        label: 'Settings',
        icon: <SettingsSvg />,
        link: '/manage-fundraising/settings',
        iconReplacement: <SettingsSvg />,
      },
  ]?.filter(Boolean),

  /**
   * SALAH SUBMENU - Shown when Salah module is clicked
   */

  salahSubmenu: [
    {
      id: 'salah-list',
      label: 'Salah List',
      icon: <SalahListsSvg />,
      link: '/manage-salah/salah',
      iconReplacement: <SalahListsSvg />,
    },
    {
      id: 'salah-monthly',
      label: 'Monthly Schedule',
      icon: <SalahMonthlySvg />,
      link: '/manage-salah/monthly',
      iconReplacement: <SalahMonthlySvg />,
      disabled: !spaceData?.salahSettingsId,
      disabledTooltipContent: 'Salah method setup required. Setup Now',
    },
    {
      id: 'salah-settings',
      label: 'Salah Settings',
      icon: <SalahSettingsSvg />,
      link: '/manage-salah/settings',
      iconReplacement: <SalahSettingsSvg />,
    },
  ],
  /**
   *Manage Settings SUBMENU - Shown when Screens module is clicked
   */
  manageSettingsSubmenu: [
    hasAnyPermission('settings', 'manageTeam') && {
      id: 'manage-settings-team',
      label: 'Team',
      icon: <TeamSvg />,
      link: '/manage-settings/team',
      iconReplacement: <TeamSvg />,
    },
    envType !== 'prod' &&
      hasAnyPermission('settings', 'manageCommunications') && {
        id: 'manage-settings-communications',
        label: 'Communications',
        icon: <CommunicationSvg />,
        link: '/manage-settings/communications',
        iconReplacement: <CommunicationSvg />,
      },
  ]?.filter(Boolean),

  /**
   * SCREENS SUBMENU - Shown when Screens module is clicked
   */
  screensSubmenu: [
    (hasAnyPermission('screens', 'viewPlaylists') ||
      hasAnyPermission('screens', 'managePlaylists')) && {
      id: 'screens-playlists',
      label: 'Playlists',
      icon: <PlaylistIcon style={{ width: '18px' }} />,
      link: '/manage-screens/playlists',
      iconReplacement: <PlaylistIcon style={{ width: '18px' }} />,
    },
    (hasAnyPermission('screens', 'viewScreens') ||
      hasAnyPermission('screens', 'manageScreens')) && {
      id: 'screens-screens',
      label: 'Screens',
      icon: <ScreenSvg style={{ width: '18px' }} />,
      link: '/manage-screens/screens',
      iconReplacement: <ScreenSvg style={{ width: '18px' }} />,
    },
    envType !== 'prod' &&
      isTheActionPermitted('screens') && {
        id: 'screens-settings',
        label: 'Settings',
        icon: <SettingsSvg />,
        link: '/manage-screens/settings',
        iconReplacement: <SettingsSvg />,
      },
  ]?.filter(Boolean),
}

/**
 * Get menu by key or return array directly
 * @param {string|array} menuKey - Menu key or menu array
 * @returns {array} Menu items array (filtered by visibility)
 */
export function getMenuByKey(menuKey) {
  if (Array.isArray(menuKey)) {
    return menuKey.filter((item) => item.visible !== false)
  }

  const menu = DYNAMIC_MENU_CONFIG[menuKey] || []
  return menu.filter((item) => item.visible !== false)
}

/**
 * Filter menu items based on enabled modules for a space
 * Used to show only modules that are enabled in space settings
 *
 * @param {array} menuItems - Menu items to filter
 * @param {array} enabledModules - Array of enabled module objects/strings
 * @returns {array} Filtered menu items
 */
export function filterMenuByEnabledModules(menuItems, enabledModules = []) {
  // If no modules specified, show all items
  if (!enabledModules || enabledModules.length === 0) {
    return menuItems
  }

  return menuItems.filter((item) => {
    // Always show Dashboard and Settings regardless of enabled modules
    if (
      item.label?.toLowerCase() === 'settings' ||
      item.label?.toLowerCase() === 'dashboard'
    ) {
      return true
    }

    // Check if this module is enabled
    return enabledModules.some((module) => {
      const moduleName = module?.name || module
      const moduleNameLower = moduleName?.toLowerCase()
      const itemLabelLower = item.label?.toLowerCase()

      // Handle "Devices" and "Kiosk" as the same module
      if (
        (moduleNameLower === 'devices' && itemLabelLower === 'kiosk') ||
        (moduleNameLower === 'kiosk' && itemLabelLower === 'devices')
      ) {
        return true
      }

      return moduleNameLower === itemLabelLower
    })
  })
}

/**
 * Find a menu item by ID in nested structure
 * Recursively searches through all menu levels
 *
 * @param {array} menuItems - Menu items to search
 * @param {string} id - ID to find
 * @returns {object|null} Found menu item or null
 */
export function findMenuItemById(menuItems, id) {
  for (const item of menuItems) {
    if (item.id === id) {
      return item
    }
    if (item.submenu) {
      const subMenuItems = getMenuByKey(item.submenu)
      const found = findMenuItemById(subMenuItems, id)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Get breadcrumb path for a menu item
 * Returns array of menu items from root to target
 *
 * @param {array} menuItems - Menu items to search
 * @param {string} id - ID to find path for
 * @param {array} path - Current path (used in recursion)
 * @returns {array|null} Array of menu items or null
 */
export function getMenuBreadcrumb(menuItems, id, path = []) {
  for (const item of menuItems) {
    const currentPath = [...path, item]

    if (item.id === id) {
      return currentPath
    }

    if (item.submenu) {
      const subMenuItems = getMenuByKey(item.submenu)
      const found = getMenuBreadcrumb(subMenuItems, id, currentPath)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Check if a path belongs to a specific menu context
 * Used to determine which menu should be displayed based on current route
 *
 * @param {string} pathname - Current pathname
 * @returns {string} Menu context key ("root" or "spaceModules")
 */
export function getMenuContextFromPath(pathname) {
  // Root paths - show root menu
  const rootPaths = ['/manage-spaces']
  if (
    rootPaths.some(
      (path) => pathname === path || pathname.startsWith(path + '/')
    )
  ) {
    return 'root'
  }

  // Space module paths - show space modules menu
  const spaceModulePaths = [
    '/manage-space-dashboard',
    '/manage-events',
    '/manage-coupons',
    '/manage-fundraising',
    '/manage-salah',
    '/manage-screens',
    '/manage-devices',
    '/manage-settings',
    '/manage-hardware',
    '/manage-devices-settings',
    '/manage-categories',
  ]

  if (spaceModulePaths.some((path) => pathname.startsWith(path))) {
    return 'spaceModules'
  }

  // Default to root
  return 'root'
}

/**
 * Find which menu item's submenu contains a given path
 * Used to determine which parent menu should be active
 *
 * @param {array} menuItems - Menu items to search
 * @param {string} pathname - Current pathname
 * @returns {object|null} Parent menu item or null
 */
export function findParentMenuByPath(menuItems, pathname) {
  for (const item of menuItems) {
    if (item.submenu) {
      const subMenuItems = getMenuByKey(item.submenu)

      // Check if any submenu item matches the path
      const matchingSubItem = subMenuItems.find(
        (subItem) => subItem.link && pathname.startsWith(subItem.link)
      )

      if (matchingSubItem) {
        return item
      }

      // Recursively check nested submenus
      const nestedParent = findParentMenuByPath(subMenuItems, pathname)
      if (nestedParent) {
        return nestedParent
      }
    }
  }
  return null
}

export default DYNAMIC_MENU_CONFIG
